
import { useEffect, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '../api/client';
import { libraryApi, tasksApi, type InsertListParams } from '../api/endpoints';
import type {
  EnrichRequest,
  Id,
  Insert,
  InsertSearchRequest,
  InsertSearchResponse,
  InsertUploadRequest,
  LibraryFacets,
  OkResponse,
  Page,
  ToolTask,
  UploadAccepted,
} from '../types/api';
import { isTaskFinished, MAX_UPLOAD_BYTES, MAX_UPLOAD_FILES } from '../types/api';
import { queryKeys } from './keys';
import { useTaskTracker, type TaskTracker } from './useTasks';

export function useInserts(params: InsertListParams = {}): UseQueryResult<Page<Insert>, ApiError> {
  return useQuery<Page<Insert>, ApiError>({
    queryKey: queryKeys.inserts(params),
    queryFn: () => libraryApi.listInserts(params),
    placeholderData: keepPreviousData,
    staleTime: 2 * 60_000,
  });
}

export function useInsertSearch(
  payload: InsertSearchRequest,
  options: { enabled?: boolean } = {},
): UseQueryResult<InsertSearchResponse, ApiError> {
  const ready = payload.q.trim().length > 0;
  return useQuery<InsertSearchResponse, ApiError>({
    queryKey: queryKeys.insertSearch(payload),
    queryFn: () => libraryApi.searchInserts(payload),
    enabled: ready && (options.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 2 * 60_000,
  });
}

export function useLibraryFacets(limit = 50): UseQueryResult<LibraryFacets, ApiError> {
  return useQuery<LibraryFacets, ApiError>({
    queryKey: queryKeys.libraryFacets(limit),
    queryFn: () => libraryApi.facets(limit),
    staleTime: 5 * 60_000,
  });
}

export function useInsert(itemId: Id | undefined): UseQueryResult<Insert, ApiError> {
  return useQuery<Insert, ApiError>({
    queryKey: queryKeys.insert(itemId ?? ''),
    queryFn: () => libraryApi.get(itemId as Id),
    enabled: Boolean(itemId),
    staleTime: 2 * 60_000,
    retry: (failureCount, error) => !error.isNotFound && failureCount < 2,
  });
}

export function useDeleteInsert() {
  const qc = useQueryClient();
  return useMutation<OkResponse, ApiError, Id>({
    mutationFn: (itemId) => libraryApi.remove(itemId),
    onSuccess: (_result, itemId) => {
      qc.removeQueries({ queryKey: queryKeys.insert(itemId) });
      void qc.invalidateQueries({ queryKey: queryKeys.libraryRoot });
    },
  });
}

export function validateUploadBatch(files: readonly File[]): string[] {
  const problems: string[] = [];
  if (files.length === 0) problems.push('Не выбрано ни одного файла');
  if (files.length > MAX_UPLOAD_FILES) {
    problems.push(`За раз можно загрузить не больше ${MAX_UPLOAD_FILES} файлов`);
  }
  const limitMb = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024));
  for (const file of files) {
    if (file.size > MAX_UPLOAD_BYTES) problems.push(`«${file.name}» больше ${limitMb} МБ`);
    if (file.size === 0) problems.push(`«${file.name}» пустой`);
  }
  return problems;
}

export const UPLOAD_CHUNK_SIZE = 10;

export interface UploadOutcomeRow {
  title: string;
  outcome: string;
  reason: string | null;
}

export interface LibraryUpload {
  upload: UseMutationResult<UploadAccepted, ApiError, InsertUploadRequest>;
  task: TaskTracker;
  sentFiles: number;
  totalFiles: number;
  rows: UploadOutcomeRow[];
}

function chunked<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let start = 0; start < items.length; start += size) {
    out.push(items.slice(start, start + size));
  }
  return out;
}

function rowsOf(task: ToolTask | undefined): UploadOutcomeRow[] {
  const raw = task?.result?.items;
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    const item = entry as Record<string, unknown>;
    return {
      title: String(item.title ?? ''),
      outcome: String(item.outcome ?? ''),
      reason: item.reason == null ? null : String(item.reason),
    };
  });
}

export function useUploadInserts(): LibraryUpload {
  const qc = useQueryClient();
  const task = useTaskTracker([queryKeys.libraryRoot]);
  const [sentFiles, setSentFiles] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [doneIds, setDoneIds] = useState<Id[]>([]);
  const [earlier, setEarlier] = useState<UploadOutcomeRow[]>([]);

  const upload = useMutation<UploadAccepted, ApiError, InsertUploadRequest>({
    mutationFn: async (payload) => {
      const groups = chunked(payload.files, UPLOAD_CHUNK_SIZE);
      setTotalFiles(payload.files.length);
      setSentFiles(0);
      setEarlier([]);
      setDoneIds([]);
      const ids: Id[] = [];
      let last: UploadAccepted | null = null;
      for (const files of groups) {
        last = await libraryApi.upload({ ...payload, files });
        ids.push(last.task.id);
        setSentFiles((done) => done + files.length);
      }
      if (last === null) throw new Error('нечего загружать');
      setDoneIds(ids.slice(0, -1));
      return last;
    },
    retry: false,
    onSuccess: (accepted) => {
      task.track(accepted.task.id);
      void qc.invalidateQueries({ queryKey: queryKeys.tasksRoot });
    },
  });

  const trackedFinished = isTaskFinished(task.task.data?.status);

  useEffect(() => {
    if (!trackedFinished || doneIds.length === 0) return;
    let alive = true;
    void Promise.all(doneIds.map((id) => tasksApi.get(id).catch(() => undefined))).then(
      (tasks) => {
        if (!alive) return;
        setEarlier(tasks.flatMap((item) => rowsOf(item)));
      },
    );
    return () => {
      alive = false;
    };
  }, [trackedFinished, doneIds]);

  const rows = useMemo(
    () => [...earlier, ...rowsOf(task.task.data)],
    [earlier, task.task.data],
  );

  return { upload, task, sentFiles, totalFiles, rows };
}

export interface LibraryEnrich {
  enrich: UseMutationResult<ToolTask, ApiError, EnrichRequest>;
  task: TaskTracker;
}

export function useEnrichLibrary(): LibraryEnrich {
  const qc = useQueryClient();
  const task = useTaskTracker([queryKeys.libraryRoot]);
  const enrich = useMutation<ToolTask, ApiError, EnrichRequest>({
    mutationFn: (payload) => libraryApi.enrich(payload),
    retry: false,
    onSuccess: (accepted) => {
      task.track(accepted.id);
      void qc.invalidateQueries({ queryKey: queryKeys.tasksRoot });
    },
  });
  return { enrich, task };
}

export function emptySearchReason(response: InsertSearchResponse | undefined): string | null {
  if (!response || response.items.length > 0) return null;
  if (response.degraded) {
    // Причина от бэка написана для инженера, поэтому наружу её не показываем:
    // монтажёру важно одно, что найдено не всё.
    return 'Нашлось не всё, попробуйте другие слова';
  }
  return null;
}

export function insertResolution(item: Insert): string | null {
  if (!item.width || !item.height) return null;
  return `${item.width}×${item.height}`;
}
